// Workspace security (consistency-pass spec §3) on the section page: the
// public keys that verify host context, one card each, the switch that
// refuses context nobody signed for, and the Security group's History. A
// top-level page: no back link.

import { Badge, SectionNav, type SectionNavItem } from "@pej/kore/components";
import { useMemo, useState } from "react";
import type { SecuritySettings } from "@/api/types";
import { SectionEditProvider, useSectionEditController } from "@/components/section-page/section-edit";
import { SectionPageShell } from "@/components/section-page/section-page-shell";
import { useUnsavedChangesGuard } from "@/components/section-page/use-unsaved-changes-guard";
import type { HistoryScope } from "@/features/audit/audit-filters";
import { HistorySection, useHistoryReveal } from "@/features/audit/entity-history";
import { EnforcementSection } from "./enforcement-section";
import { KeysSection, keyNavItems } from "./keys-section";
import { SECURITY_SECTION } from "./security-sections";

const SECURITY_HISTORY: HistoryScope = { groups: ["Security"] };

export interface SecurityPageProps {
    workspaceId: string;
    settings: SecuritySettings;
}

export function SecurityPage({ workspaceId, settings }: SecurityPageProps) {
    return (
        <SectionEditProvider>
            <SecurityPageBody workspaceId={workspaceId} settings={settings} />
        </SectionEditProvider>
    );
}

function SecurityPageBody({ workspaceId, settings }: SecurityPageProps) {
    const controller = useSectionEditController();
    const { dialog } = useUnsavedChangesGuard(controller.isActiveSectionDirty);
    const [draft, setDraft] = useState(false);
    const history = useHistoryReveal(SECURITY_SECTION.history);
    const navItems: SectionNavItem[] = useMemo(
        () => [
            ...keyNavItems(settings.keys, draft, controller.errorsBySection),
            { id: SECURITY_SECTION.enforcement, label: "Enforcement" },
            { id: SECURITY_SECTION.history, label: "History" },
        ],
        [settings.keys, draft, controller.errorsBySection],
    );
    return (
        <SectionPageShell
            title="Security"
            badges={
                settings.requireSignedContext ? (
                    <Badge tone="success">Signed context required</Badge>
                ) : (
                    <Badge tone="neutral">Unsigned context accepted</Badge>
                )
            }
            nav={<SectionNav items={navItems} sticky={false} />}
            onNavClick={history.onNavClick}
        >
            <KeysSection workspaceId={workspaceId} settings={settings} draft={draft} onDraft={setDraft} />
            <EnforcementSection workspaceId={workspaceId} settings={settings} />
            <HistorySection
                workspaceId={workspaceId}
                sectionId={SECURITY_SECTION.history}
                scope={SECURITY_HISTORY}
                revealed={history.revealed}
                onReveal={history.reveal}
            />
            {dialog}
        </SectionPageShell>
    );
}
